import React, { createContext, useContext, useState, useCallback, useRef, ReactNode } from 'react';
import { exportTimeline, ExportProgress, ExportCanceledError, ExportTooLargeError, MAX_EXPORT_CLIPS } from '@/lib/videoExport';
import { Video } from '@/types/Video';

/**
 * Single-task export progress state, mirroring UploadProgressContext but for the
 * one-at-a-time "Download movie" render. Owns the AbortController so the toast's
 * Cancel button can stop an in-flight export.
 */

type ExportStatus = 'idle' | 'running' | 'success' | 'error';

interface ExportProgressState {
  status: ExportStatus;
  progress: ExportProgress | null;
  /** Friendly error message for the toast (null when none). */
  errorMessage: string | null;
}

interface StartExportArgs {
  filteredVideos: Video[];
  projectName: string;
}

interface ExportProgressContextType extends ExportProgressState {
  /** Begin an export. No-op if one is already running. */
  startExport: (args: StartExportArgs) => Promise<void>;
  /** Cancel the in-flight export (if any). */
  cancelExport: () => void;
  /** Dismiss the toast after success/error. */
  dismiss: () => void;
}

const ExportProgressContext = createContext<ExportProgressContextType | undefined>(undefined);

export function ExportProgressProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ExportProgressState>({
    status: 'idle',
    progress: null,
    errorMessage: null,
  });
  const abortRef = useRef<AbortController | null>(null);
  const runningRef = useRef(false);

  const cancelExport = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const dismiss = useCallback(() => {
    setState({ status: 'idle', progress: null, errorMessage: null });
  }, []);

  const startExport = useCallback(async ({ filteredVideos, projectName }: StartExportArgs) => {
    if (runningRef.current) return; // Only one export at a time.
    runningRef.current = true;

    const controller = new AbortController();
    abortRef.current = controller;

    setState({
      status: 'running',
      progress: { phase: 'preparing', percent: 0, detail: 'Preparing export…' },
      errorMessage: null,
    });

    try {
      await exportTimeline({
        filteredVideos,
        projectName,
        signal: controller.signal,
        onProgress: (progress) => {
          setState((prev) => ({ ...prev, progress }));
        },
      });
      setState((prev) => ({ ...prev, status: 'success' }));
    } catch (err) {
      if (err instanceof ExportCanceledError) {
        // User-initiated cancel: just clear the toast, no error.
        setState({ status: 'idle', progress: null, errorMessage: null });
      } else if (err instanceof ExportTooLargeError) {
        setState({
          status: 'error',
          progress: null,
          errorMessage: `This project has ${err.clipCount} clips, which is too many to download in the browser (limit ${MAX_EXPORT_CLIPS}). Apply a date filter to export a smaller range.`,
        });
      } else {
        console.error('[Export] Failed:', err);
        setState({
          status: 'error',
          progress: null,
          errorMessage: 'Something went wrong while creating your movie. Please try again.',
        });
      }
    } finally {
      runningRef.current = false;
      abortRef.current = null;
    }
  }, []);

  return (
    <ExportProgressContext.Provider value={{ ...state, startExport, cancelExport, dismiss }}>
      {children}
    </ExportProgressContext.Provider>
  );
}

export function useExportProgress() {
  const context = useContext(ExportProgressContext);
  if (!context) {
    throw new Error('useExportProgress must be used within ExportProgressProvider');
  }
  return context;
}
